import { inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TransactionService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';
import { map, catchError, of, delayWhen, timer, mergeMap, tap, exhaustMap, finalize, expand, EMPTY, reduce, takeUntil } from 'rxjs';
import { ToastActions } from '../toast-feature';
import { TransactionsActions } from './transaction-feature.actions';
import { transactionFeature } from 'src/app/+state/transaction-feature/transaction-feature.state';
import { selectRouteParams } from '@store';
import { Router } from '@angular/router';
import { TransactionHistory, TransactionHistoryList } from '@shared/models';
import { flatten } from 'lodash-es';

export const loadTransactions$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), transactionService = inject(TransactionService)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadTransactions),
      concatLatestFrom(() => [store.select(transactionFeature.selectTransactionsActiveFilters)]),
      mergeMap(([payload, activeFilters]) => {
        return transactionService.getTransactions({ ...payload.params, activeFilters }).pipe(
          map((TransactionsResponse) => {
            return TransactionsActions.loadTransactionsSuccess({
              transactions: TransactionsResponse.items,
              totalElements: TransactionsResponse.totalElements,
            });
          }),
          catchError((error) => {
            return of(TransactionsActions.loadTransactionsFailure({ error }));
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const loadTransactionsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadTransactionsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('TransactionsActions.loadTransactions', error);
        return ToastActions.error({ message: 'Unable to fetch Transactions.' });
      }),
    );
  },
  { functional: true },
);

export const loadBusinessAccountTransactions$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), transactionService = inject(TransactionService)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadBusinessAccountTransactions),
      concatLatestFrom(() => [store.select(selectRouteParams), store.select(transactionFeature.selectTransactionsActiveFilters)]),
      mergeMap(([payload, routeParams, activeFilters]) => {
        return transactionService
          .getTransactions({ ...payload.params, activeFilters, businessAccountId: routeParams.businessAccountId })
          .pipe(
            map((TransactionsResponse) => {
              return TransactionsActions.loadBusinessAccountTransactionsSuccess({
                transactions: TransactionsResponse.items,
                totalElements: TransactionsResponse.totalElements,
              });
            }),
            catchError((error) => {
              return of(TransactionsActions.loadBusinessAccountTransactionsFailure({ error }));
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const loadBusinessAccountTransactionsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadBusinessAccountTransactionsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('TransactionsActions.loadBusinessAccountTransactions', error);
        return ToastActions.error({ message: 'Unable to fetch Business Account Transactions.' });
      }),
    );
  },
  { functional: true },
);

export const loadTransactionDetails$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), transactionService = inject(TransactionService)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadTransactionDetails),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      mergeMap(([, routeParams]) => {
        return transactionService.getTransactionById(routeParams.transactionId).pipe(
          map((transactionDetails) => {
            return TransactionsActions.loadTransactionDetailsSuccess({ transactionDetails });
          }),
          catchError((error) => {
            return of(TransactionsActions.loadBusinessAccountTransactionsFailure({ error }));
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const loadTransactionDetailsError$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadTransactionDetailsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('TransactionsActions.loadTransactionDetails', error);
        return ToastActions.error({ message: 'Unable to fetch Transaction Details.' });
      }),
    );
  },
  { functional: true },
);

export const loadTransactionDetailsFailureRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadTransactionDetailsFailure),
      tap({
        next: () => router.navigateByUrl('/app/transactions'),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const loadTransactionHistory$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), transactionService = inject(TransactionService)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadTransactionDetailsSuccess),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      mergeMap(([payload, routeParams]) => {
        let historyPageNumber = 0;
        const transactionId = routeParams.transactionId;
        return transactionService.getTransactionHistory({ transactionId, size: 200, page: historyPageNumber }).pipe(
          expand((response) => {
            if (response.hasNext) {
              historyPageNumber += 1;

              return transactionService.getTransactionHistory({
                transactionId: transactionId!,
                size: 200,
                page: historyPageNumber,
              });
            } else {
              return EMPTY;
            }
          }),
          reduce<TransactionHistoryList, TransactionHistory[]>((acc, current) => acc.concat(current.items), []),
          map((response) =>
            TransactionsActions.loadTransactionHistorySuccess({
              transactionHistory: flatten(response),
              businessAccountId: payload.transactionDetails?.businessAccountId,
            }),
          ),
          catchError((error) => {
            ErrorUtils.catchError('transactionService.getTransactionHistory error', error);
            return of(TransactionsActions.loadTransactionHistoryFailure({ error }));
          }),
          takeUntil(actions$.pipe(ofType(TransactionsActions.loadTransactionDetails))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadTransactionLedgerEntries$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), transactionService = inject(TransactionService)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadLedgerEntries),
      concatLatestFrom(() => [store.select(selectRouteParams), store.select(transactionFeature.selectLedgerEntriesActiveFilters)]),
      mergeMap(([payload, routeParams, activeFilters]) => {
        return transactionService
          .getLedgerEntries({ ...payload.params, activeFilters: { ...activeFilters, transactionId: routeParams.transactionId } })
          .pipe(
            map((financialAccountActivity) => {
              return TransactionsActions.loadLedgerEntriesSuccess(financialAccountActivity);
            }),
            catchError((error) => {
              return of(TransactionsActions.loadLedgerEntriesFailure({ error }));
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const loadLedgerEntriesFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadLedgerEntriesFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('TransactionsActions.loadLedgerEntriesFailure', error);
        return ToastActions.error({ message: `Unable to load Ledger Entries.` });
      }),
    );
  },
  { functional: true },
);

export const loadJournalActivity$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), transactionService = inject(TransactionService)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadJournalActivity),
      concatLatestFrom(() => [store.select(selectRouteParams), store.select(transactionFeature.selectJournalActivityFiltersRequest)]),
      mergeMap(([payload, routeParams, activeFilters]) => {
        return transactionService.getJournalActivity({ ...payload.params, activeFilters }, routeParams.transactionId).pipe(
          map((journalActivity) => {
            return TransactionsActions.loadJournalActivitySuccess(journalActivity);
          }),
          catchError((error) => {
            return of(TransactionsActions.loadJournalActivityFailure({ error }));
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const loadJournalActivityFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(TransactionsActions.loadJournalActivityFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('TransactionsActions.loadJournalActivityFailure', error);
        return ToastActions.error({ message: `Unable to load Journal Activities.` });
      }),
    );
  },
  { functional: true },
);

export const updateTransactionStatus$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), transactionService = inject(TransactionService)) => {
    return actions$.pipe(
      ofType(TransactionsActions.updateTransactionStatus),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([payload, routeParams]) => {
        return transactionService.updateTransactionStatus(routeParams.transactionId, payload.params).pipe(
          map(() => TransactionsActions.updateTransactionStatusSuccess()),
          finalize(() => {
            store.dispatch(TransactionsActions.loadTransactionDetails());
          }),
          catchError((error) => of(TransactionsActions.updateTransactionStatusError({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const updateTransactionStatusFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(TransactionsActions.updateTransactionStatusError),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('TransactionsActions.updateTransactionStatusError', error);
        return ToastActions.error({ message: `Unable to update transaction status.` });
      }),
    );
  },
  { functional: true },
);
